// Resources related to GCP identity-aware proxy
import { Dialog } from '@material-ui/core';
import { Box, Button, Heading, Icons, Stack, Text, ThemeContext } from '@vouched.id/vault';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appConfig } from 'config';
import { clearIapAuthRequired, logout } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
const SESSION_POLL_INTERVAL_MS = 500;
const SESSION_POLL_TIMEOUT_MS = 120_000;
/** Callers block on the probe, so a silent socket aborts to 'unreachable'. */
const PROBE_TIMEOUT_MS = 5000;
// onboard-service also serves /admin/ routes, on another origin and with no IAP.
export function isIapProtectedUrl(url) {
    return Boolean(url && appConfig.api && url.startsWith(`${appConfig.api}/admin/`));
}
// IAP guards the /admin path rule, not the whole host.
function getIapAuthUrl() {
    return `${appConfig.api}/admin?gcp-iap-mode=DO_SESSION_REFRESH`;
}
const IAP_QUERY = /[?&](?:gcp[-_]iap[-_]mode|__gcp_iap)/i;
/**
 * api-service never redirects, but IAP answers an expired cookie with a 302
 * even when X-Requested-With is set. XHR follows it, so only the final URL
 * shows that it happened.
 */
export const redirectedByIap = (requestedUrl, responseUrl) => {
    if (!requestedUrl || !responseUrl) {
        return false;
    }
    try {
        const requested = new URL(requestedUrl);
        const answered = new URL(responseUrl);
        return (requested.origin !== answered.origin ||
            requested.pathname !== answered.pathname ||
            IAP_QUERY.test(answered.search));
    }
    catch {
        return false;
    }
};
/*
 * x-goog-iap-generated-response is unreadable cross-origin: IAP sends no
 * Access-Control-Expose-Headers. Content-Type is CORS-safelisted and separates
 * them — IAP answers text/html, api-service application/json.
 */
export const refusalSource = (headers) => {
    if (!headers) {
        return 'unknown';
    }
    if (headers['x-goog-iap-generated-response']) {
        return 'iap';
    }
    const contentType = headers['content-type'];
    if (typeof contentType !== 'string') {
        return 'unknown';
    }
    if (contentType.includes('application/json')) {
        return 'app';
    }
    return contentType.includes('text/html') ? 'iap' : 'unknown';
};
/*
 * Unclaimed path: api-service answers 404, so a 401/403 can only be IAP's. Must
 * stay tokenless — an Authorization header makes api-service answer 401 itself.
 */
export const probeIapSession = async () => {
    try {
        const response = await fetch(`${appConfig.api}/admin/iap-session-probe`, {
            credentials: 'include',
            // X-Requested-With makes IAP answer 401 rather than redirect, mostly.
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            method: 'GET',
            redirect: 'manual',
            signal: AbortSignal.timeout(PROBE_TIMEOUT_MS)
        });
        // Only IAP redirects this path: it is how an expired cookie is answered.
        if (response.type === 'opaqueredirect') {
            return 'refused';
        }
        if (response.ok || response.status === 404) {
            return 'session';
        }
        // A fetch Response carries a plain number, not axios's enum.
        if (response.status === 401 || response.status === 403) {
            return 'refused';
        }
        // A 5xx says nothing either way.
        return 'unreachable';
    }
    catch {
        return 'unreachable';
    }
};
const hasIapSession = async () => (await probeIapSession()) === 'session';
/*
 * onboard-service takes the same bearer token and sits behind no IAP, so it
 * separates an expired session from a missing permission. api-service answers
 * 401 for both.
 */
const appSessionState = async (accessToken) => {
    try {
        const response = await fetch(`${appConfig.onboardApi}/api/user/info`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Requested-With': 'XMLHttpRequest'
            },
            method: 'GET',
            signal: AbortSignal.timeout(PROBE_TIMEOUT_MS)
        });
        if (response.ok) {
            return 'valid';
        }
        return response.status === 401 ? 'expired' : 'unknown';
    }
    catch {
        return 'unknown';
    }
};
export const IapAuthPrompt = ({ onDismiss }) => {
    const { tokens } = useContext(ThemeContext).theme;
    const { dispatch, state } = useGlobalState();
    const { accessToken, iapAuthDetails: details, iapAuthUnreadable: unreadable } = state;
    const [waiting, setWaiting] = useState(false);
    const [blocked, setBlocked] = useState(false);
    const [gaveUp, setGaveUp] = useState(false);
    const [denied, setDenied] = useState(false);
    const timer = useRef();
    const probing = useRef(false);
    const live = useRef(true);
    const popup = useRef(null);
    const unreadableNow = useRef(unreadable);
    const navigate = useNavigate();
    useEffect(() => {
        unreadableNow.current = unreadable;
    }, [unreadable]);
    const stopPolling = useCallback(() => {
        window.clearInterval(timer.current);
        timer.current = undefined;
        probing.current = false;
    }, []);
    useEffect(() => () => {
        live.current = false;
        stopPolling();
        popup.current?.close();
    }, [stopPolling]);
    /** IAP is satisfied, so the refusal came from the app's own auth. */
    useEffect(() => {
        if (unreadable) {
            return;
        }
        void hasIapSession().then(async (established) => {
            if (!established) {
                return;
            }
            const session = await appSessionState(accessToken);
            if (!live.current) {
                return;
            }
            if (session === 'expired') {
                dispatch(logout());
                navigate('/signin');
            }
            else if (session === 'valid') {
                setDenied(true);
            }
        });
    }, [accessToken, dispatch, navigate, unreadable]);
    const handleClick = useCallback(() => {
        const win = window.open(getIapAuthUrl(), 'vouched-iap-auth', 'width=520,height=640');
        if (!win) {
            setBlocked(true);
            return;
        }
        popup.current = win;
        setBlocked(false);
        setGaveUp(false);
        setWaiting(true);
        const startedAt = Date.now();
        const timedOut = () => Date.now() - startedAt > SESSION_POLL_TIMEOUT_MS;
        // The popup is cross-origin; its progress cannot be inspected.
        timer.current = window.setInterval(() => {
            if (unreadableNow.current) {
                if (win.closed) {
                    stopPolling();
                    setWaiting(false);
                    dispatch(clearIapAuthRequired());
                    onDismiss();
                }
                else if (timedOut()) {
                    stopPolling();
                    setWaiting(false);
                    setGaveUp(true);
                }
                return;
            }
            if (probing.current) {
                return;
            }
            probing.current = true;
            const { closed } = win;
            void hasIapSession().then((established) => {
                probing.current = false;
                if (!live.current) {
                    return;
                }
                if (established) {
                    stopPolling();
                    win.close();
                    setWaiting(false);
                    dispatch(clearIapAuthRequired());
                    onDismiss();
                    return;
                }
                if (closed) {
                    stopPolling();
                    setWaiting(false);
                    return;
                }
                if (timedOut()) {
                    stopPolling();
                    setWaiting(false);
                    setGaveUp(true);
                }
            });
        }, SESSION_POLL_INTERVAL_MS);
    }, [dispatch, onDismiss, stopPolling]);
    return (React.createElement(Dialog, { fullWidth: true, maxWidth: "sm", onClose: onDismiss, open: true },
        React.createElement("div", { style: {
                backgroundColor: tokens.colors.backgroundSecondary,
                boxSizing: 'border-box',
                padding: tokens.spacing.large,
                width: '100%'
            } },
            React.createElement(Stack, { spacing: "spacing.default" },
                React.createElement(Box, { alignItems: "center", display: "flex" },
                    React.createElement(Icons.Lock, { color: tokens.colors.accentPrimary, height: 20, title: denied ? 'Access denied' : 'Protected by IAP', width: 20 }),
                    React.createElement(Heading, { style: {
                            fontWeight: tokens.text.weight.bold,
                            marginLeft: tokens.spacing.xsmall
                        }, tag: "h2", variation: "h4" }, denied ? 'Not available to your account' : 'Sign in to continue')),
                React.createElement(Text, { style: { color: tokens.colors.textBody }, variation: "body" }, denied
                    ? 'Your Google sign-in is in order. This page needs a Vouched permission your account does not have.'
                    : 'This page requires additional authentication with your Google Workspace account.'),
                !denied && (React.createElement(Box, null,
                    React.createElement(Button, { disabled: waiting, iconLeft: waiting ? React.createElement(Icons.Loading, null) : undefined, onClick: handleClick, size: "admin", variation: waiting ? 'loading' : 'primary' }, waiting ? 'Waiting for sign-in' : 'Sign in with Google'))),
                !denied && unreadable && (React.createElement(Text, { style: { color: tokens.colors.textMuted }, variation: "caption" }, "Close the sign-in window when you are done and this page will load.")),
                blocked && (React.createElement(Text, { style: { color: tokens.colors.errorPrimary }, variation: "caption" }, "The sign-in window was blocked. Allow pop-ups for this site, then try again.")),
                gaveUp && (React.createElement(Text, { style: { color: tokens.colors.errorPrimary }, variation: "caption" }, "The sign-in did not complete. If your browser is signed in to a Google account without access, sign out of it and try again.")),
                details !== null && (React.createElement("details", { style: {
                        borderTop: `1px solid ${tokens.colors.lightPurple}`,
                        paddingTop: tokens.spacing.default
                    } },
                    React.createElement("summary", { style: {
                            color: tokens.colors.interactivePrimary,
                            cursor: 'pointer',
                            fontSize: tokens.text.size.small
                        } }, "More details"),
                    React.createElement(Text, { "data-ph-mask": true, style: {
                            color: tokens.colors.textMuted,
                            display: 'block',
                            marginTop: tokens.spacing.xsmall,
                            overflowWrap: 'anywhere'
                        }, variation: "caption" }, details.status === null
                        ? details.message
                        : `HTTP ${details.status}. ${details.message}`)))))));
};
